import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const TYPES = [
  { k: "audiobook", label: "Audiobook", accept: "audio/mpeg,audio/mp3,.mp3", note: "mp3 only" },
  { k: "podcast",   label: "Podcast",   accept: "audio/mpeg,audio/mp3,.mp3", note: "mp3 only" },
  { k: "video",     label: "Video",     accept: "video/mp4,.mp4",            note: "mp4 only" },
];

const fmtDur = (s) => {
  if (!s || s < 0) return "0:00";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
};

const readMediaDuration = (file) => new Promise((resolve, reject) => {
  const isVideo = file.type.startsWith("video/");
  const el = document.createElement(isVideo ? "video" : "audio");
  el.preload = "metadata";
  el.onloadedmetadata = () => {
    const d = el.duration;
    URL.revokeObjectURL(el.src);
    if (!isFinite(d) || d <= 0) reject(new Error("Could not read duration"));
    else resolve(d);
  };
  el.onerror = () => reject(new Error("Unreadable media"));
  el.src = URL.createObjectURL(file);
});

export default function SellStream() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [type, setType] = useState("audiobook");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("6");
  const [imageUrl, setImageUrl] = useState("");
  const [chapters, setChapters] = useState([]); // {title, storage_key, duration_seconds, mime, size_bytes, uploading}
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (!loading && !user) nav("/login");
    else if (!loading && user && !(user.role === "seller" || user.role === "admin")) nav("/");
  }, [loading, user, nav]);

  const totalSec = chapters.reduce((s, c) => s + (c.duration_seconds || 0), 0);
  const over2h = totalSec > 7200 + 0.5;
  const priceNum = parseFloat(price);
  const priceInvalid = isNaN(priceNum) || priceNum < 0;

  const currentType = TYPES.find((t) => t.k === type);

  const onFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (chapters.length + files.length > 20) {
      toast.error("Maximum 20 chapters per sale");
      return;
    }
    for (const f of files) {
      const badMime = type === "video" ? !f.type.includes("mp4") : !f.type.includes("mpeg") && !f.type.includes("mp3");
      if (badMime) { toast.error(`${f.name}: wrong format for ${currentType.label} (${currentType.note})`); continue; }
      if (f.size > 1024 * 1024 * 1024) { toast.error(`${f.name}: exceeds 1 GB cap`); continue; }
      let duration;
      try { duration = await readMediaDuration(f); }
      catch { toast.error(`${f.name}: could not read duration`); continue; }
      const stub = { title: f.name.replace(/\.[^.]+$/, ""), duration_seconds: duration, size_bytes: f.size, uploading: true, mime: f.type };
      setChapters((cs) => [...cs, stub]);
      const idx = chapters.length + files.indexOf(f);
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("duration_seconds", String(duration));
        fd.append("title", stub.title);
        const { data } = await api.post("/streams/upload-chapter", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setChapters((cs) => cs.map((c, i) => i === idx ? { ...data, uploading: false } : c));
        toast.success(`Chapter uploaded: ${data.title}`);
      } catch (err) {
        toast.error(`${f.name}: upload failed — ${err.response?.data?.detail || err.message}`);
        setChapters((cs) => cs.filter((_, i) => i !== idx));
      }
    }
  };

  const removeChapter = (i) => setChapters((cs) => cs.filter((_, j) => j !== i));
  const setChapterTitle = (i, t) => setChapters((cs) => cs.map((c, j) => j === i ? { ...c, title: t } : c));

  const uploadCover = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/uploads/product-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageUrl(data.url);
      toast.success("Cover uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Title required");
    if (!description.trim()) return toast.error("Description required");
    if (priceInvalid) return toast.error("Price must be > $0 and ≤ $10");
    if (chapters.length === 0) return toast.error("Upload at least one chapter");
    if (chapters.some((c) => c.uploading)) return toast.error("Wait for uploads to finish");
    if (over2h) return toast.error("Combined duration exceeds 2 hours");
    setBusy(true);
    try {
      await api.post("/streams", {
        title, description, price: priceNum, stream_type: type,
        image_url: imageUrl || null,
        chapters: chapters.map((c) => ({
          title: c.title || "Untitled",
          storage_key: c.storage_key,
          duration_seconds: c.duration_seconds,
          mime: c.mime,
          size_bytes: c.size_bytes,
        })),
      });
      toast.success("Stream submitted for admin review");
      nav("/seller");
    } catch (err) { toast.error(err.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };

  if (!user) return null;

  return (
    <Layout>
      <section className="max-w-[1100px] mx-auto px-6 py-10">
        <div className="mono text-xs uppercase tracking-[0.3em] text-[#C77E3C]">/ Sell on Juba — Streams</div>
        <h1 data-testid="sell-stream-heading" className="heading text-4xl sm:text-5xl font-black uppercase tracking-tighter mt-2">
          For Audiobooks, Podcasts and Videos
        </h1>
        <p className="mt-3 text-[#555] max-w-2xl">Upload 1 to 20 sections or chapters — a single sale runs up to 2 hours total. Buyers stream on Jubasmash: no downloads, buy once, listen for life. To go beyond 2 hours, create a &ldquo;Part 2&rdquo; listing linked by title (e.g. <i>The Last of the Porcelain Warriors, Part 2</i>).</p>

        <div className="mt-6 brut-border bg-[#FDF3E9] p-4">
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">Content policy</div>
          <p className="text-sm mt-2 leading-relaxed">No pornographic material and no scenes of excess violence. Submissions that violate this are rejected and repeat offences remove your seller account.</p>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C] mt-4">Preview</div>
          <p className="text-sm mt-2 leading-relaxed">Buyers can preview the first <b>3 minutes</b> of any chapter free before purchase — bake in a strong hook up-front.</p>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C] mt-4">Price</div>
          <p className="text-sm mt-2 leading-relaxed">Recommended: <b>$6</b>. You keep 70%, Jubasmash keeps 30%.</p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
          {TYPES.map((t) => (
            <button key={t.k} data-testid={`stream-type-${t.k}`} onClick={() => { setType(t.k); setChapters([]); }}
              className={`brut-border p-4 text-left ${type === t.k ? "bg-[#C77E3C] text-white" : "bg-white hover:bg-[#FDF3E9]"}`}>
              <div className="mono text-[10px] uppercase tracking-[0.3em] opacity-70">{t.note}</div>
              <div className="heading text-2xl font-bold uppercase mt-1">{t.label}</div>
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-4">
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.3em] text-[#555]">Title</label>
            <input data-testid="stream-title" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full mt-1 brut-border bg-white px-3 py-2" placeholder={type === "audiobook" ? "The Last of the Porcelain Warriors" : type === "podcast" ? "Kitchen Talk, Ep. 1" : "Lagos in 12 Minutes"} />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.3em] text-[#555]">Description</label>
            <textarea data-testid="stream-description" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full mt-1 brut-border bg-white px-3 py-2" placeholder="What is it, who's it for, what will the listener feel?" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mono text-[10px] uppercase tracking-[0.3em] text-[#555]">Price (USD)</label>
              <input data-testid="stream-price" type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className={`w-full mt-1 brut-border px-3 py-2 ${priceInvalid ? "bg-[#FBEDE0]" : "bg-white"}`} />
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C] mt-1">recommended: $6</div>
            </div>
            <div>
              <label className="mono text-[10px] uppercase tracking-[0.3em] text-[#555]">Cover image (optional)</label>
              <input data-testid="stream-cover" type="file" accept="image/*" onChange={uploadCover} className="w-full mt-1 brut-border bg-white px-3 py-2 text-sm" />
              {imageUrl && <img src={imageUrl} alt="cover" className="mt-2 h-24 brut-border object-cover" />}
            </div>
          </div>
        </div>

        <div className="mt-10">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="heading text-2xl font-bold uppercase">Chapters ({chapters.length}/20)</h2>
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#555] mt-1">
                Total: <span className={over2h ? "text-[#C77E3C] font-bold" : ""}>{fmtDur(totalSec)}</span> / 2:00:00
              </div>
            </div>
            <button data-testid="pick-chapters" onClick={() => fileRef.current?.click()} className="brut-border bg-[#1F3D2A] text-white px-4 py-3 mono text-xs uppercase tracking-[0.25em] hover:bg-[#C77E3C]" disabled={chapters.length >= 20}>+ Add {type === "video" ? "video" : "audio"} chapters</button>
            <input ref={fileRef} type="file" multiple accept={currentType.accept} onChange={onFilesPicked} className="hidden" />
          </div>

          <div className="mt-4 brut-border bg-white">
            {chapters.length === 0 ? (
              <div className="p-10 text-center mono text-xs uppercase tracking-[0.3em] text-[#555]">No chapters yet — click &ldquo;+ Add&rdquo; above.</div>
            ) : chapters.map((c, i) => (
              <div key={i} data-testid={`chapter-row-${i}`} className={`p-4 flex items-center gap-3 ${i > 0 ? "border-t-2 border-[#1F3D2A]" : ""}`}>
                <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C] w-8">{String(i + 1).padStart(2, "0")}</div>
                <input value={c.title} onChange={(e) => setChapterTitle(i, e.target.value)} className="flex-1 brut-border bg-[#FDF3E9] px-3 py-2 text-sm" />
                <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#555] w-20 text-right">{fmtDur(c.duration_seconds)}</div>
                <div className="mono text-[9px] uppercase tracking-[0.2em] text-[#555] w-24 text-right">{(c.size_bytes / (1024 * 1024)).toFixed(1)} MB</div>
                {c.uploading ? (
                  <span className="mono text-[9px] uppercase tracking-[0.2em] text-[#C77E3C] w-24 text-center">Uploading…</span>
                ) : (
                  <button data-testid={`remove-chapter-${i}`} onClick={() => removeChapter(i)} className="brut-border bg-white px-3 py-1 mono text-[10px] uppercase tracking-[0.25em] hover:bg-[#C77E3C] hover:text-white w-24">Remove</button>
                )}
              </div>
            ))}
          </div>
          {over2h && <div className="mt-2 mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">Over 2 hours — split into a Part 2 listing.</div>}
        </div>

        <div className="mt-10 flex items-center gap-3">
          <button data-testid="submit-stream" onClick={submit} disabled={busy || chapters.length === 0 || over2h || priceInvalid} className="brut-border bg-[#C77E3C] text-white px-6 py-4 heading uppercase tracking-tight font-black hover:bg-[#1F3D2A] disabled:opacity-40">
            {busy ? "Submitting…" : "Submit for admin review →"}
          </button>
          <button onClick={() => nav("/seller")} className="brut-border bg-white px-6 py-4 mono uppercase text-xs tracking-[0.25em]">Cancel</button>
        </div>
      </section>
    </Layout>
  );
}
